import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import CustomerOverview from "../components/CustomerDashboard/CustomerOverview";
import PrescriptionHistory from "../components/CustomerDashboard/PrescriptionHistory";
import InvoiceHistory from "../components/CustomerDashboard/InvoiceHistory";
import RefillReminders from "../components/CustomerDashboard/RefillReminders";
import ManualRefillReminders from "../components/CustomerDashboard/ManualRefillReminders";
import CustomerNotes from "../components/CustomerDashboard/CustomerNotes";
import CustomerAttachments from "../components/CustomerDashboard/CustomerAttachments";
import { useParams } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import Customer from "./Customer";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";

const CustomerDashboard = () => {
    const { id } = useParams();
    const [customer, setCustomer] = useState(null);
    const [stats, setStats] = useState({});
    const navigate = useNavigate();
    const [relativesDialogVisible, setRelativesDialogVisible] = useState(false);
    const [relatives, setRelatives] = useState([]);
    const [initCust, setInitCust] = useState([]);
    const { push, beginDrilldown } = useBreadcrumbs();
    const [portalLink, setPortalLink] = useState(null);

    // Set manually via Django admin per-customer (gf/models.py::OrganizationSettings.crm_only).
    // Cached in localStorage by Layout.js on load — avoids a second API call here.
    const crmOnly = (() => {
        try {
            return JSON.parse(localStorage.getItem('organizationSettings') || '{}')?.crm_only === true;
        } catch {
            return false;
        }
    })();
    const [sendingOrderReady, setSendingOrderReady] = useState(false);

    const handleSendOrderReady = async () => {
        if (!customer || sendingOrderReady) return;
        setSendingOrderReady(true);
        try {
            await axiosInstance.post(`/api/customers/${id}/order-ready-sms/`);
            handleApiSuccess(`Order ready SMS sent to ${customer.first_name || ''} ${customer.last_name}.`.trim());
        } catch (error) {
            handleApiError(error);
        } finally {
            setSendingOrderReady(false);
        }
    };

    const handleCreateInvoice = () => {
        if (customer) {
            const customerWithLabel = {
                ...customer,
                displayLabel: `${customer.first_name} ${customer.last_name}`,
            };
            beginDrilldown();
            navigate("/invoices", { state: { customer: customerWithLabel } });
            push({
                label: `Customer: ${customerWithLabel.displayLabel}`,
                path: `/customers/${customerWithLabel.id}`,
            });
            push({
                label: `Invoices`,
                path: `/invoices`,
            });
            push({
                label: `Invoice: new`,
            });
        }
    };

    const handleCreatePrescription = () => {
        if (customer) {
            const customerWithLabel = {
                ...customer,
                displayLabel: `${customer.first_name} ${customer.last_name}`,
            };
            beginDrilldown();
            navigate("/prescriptions", { state: { customer: customerWithLabel } });
            push({
                label: `Customer: ${customerWithLabel.displayLabel}`,
                path: `/customers/${customerWithLabel.id}`,
            });
            push({
                label: `Prescriptions`,
                path: `/prescriptions`,
            });
            push({
                label: `Prescription: new`,
            });
        }
    };

    const handleUpdateCustomer = () => {
        beginDrilldown();
        navigate("/customers", { state: { customer } });
        push({
            label: `Customer: ${customer.last_name}`,
            path: `/customers/${customer.id}`,
        });
        push({
            label: `Customers`,
            path: `/customers`,
        });
        push({
            label: `Customer: ${customer.last_name}`,
            path: '/customers',
            state: { customer } 
        });
    };

    const handleAddRelative = () => {
        beginDrilldown();
        navigate("/customers", { state: { relative: customer } });
        push({
            label: `Customer: ${customer.last_name}`,
            path: `/customers/${customer.id}`,
        });
        push({
            label: `Customers`,
            path: `/customers`,
        });
        push({
            label: `Customer: ${customer.last_name}`,
            path: '/customers',
            state: { customer } 
        });
    };

    const fetchPortalLink = async () => {
        try {
            const response = await axiosInstance.get(`/api/customers/${id}/portal-link/`);
            setPortalLink(response.data);
        } catch (error) {
            setPortalLink(null); // not linked — not an error
        }
    };

    // grant handler:
    const handleGrantHistory = async () => {
        try {
            const response = await axiosInstance.post(`/api/customers/${id}/grant-history/`);
            setPortalLink(prev => ({
                ...prev,
                history_access:     response.data.history_access,
                history_granted_at: response.data.history_granted_at,
            }));
        } catch (error) {
            handleApiError(error);
        }
    };

    useEffect(() => {
        fetchCustomerData();
        fetchStats();
        fetchPortalLink();
    }, [id]);

    const fetchCustomerData = async () => {
        try {
            const response = await axiosInstance.get(`/api/customer/${id}/`);
            setCustomer(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const fetchStats = async () => {
        try {
          const response = await axiosInstance.get(`/api/customers/${id}/statistics/`);
          setStats(response.data);
        } catch (error) {
          handleApiError(error);
        }
      };
    
      if (!stats) return <p>Loading statistics...</p>;
    
    const STATUS_LABELS = {
        Open: "Pending",
        Complete: "Completed",
        Cancelled: "Cancelled",
    };

    // Fixed colors for key statuses
    const STATUS_COLORS = {
        Complete: "#4CAF50", // Green
        Cancelled: "#F44336", // Red
    };

    // Fallback colors for other statuses (cycled)
    const FALLBACK_COLORS = [
        "#FFC107", "#03A9F4", "#9C27B0", "#795548", "#009688", "#E91E63"
    ];

    // Build pie data from API
    const invoicePieData = Object.entries(stats?.invoices?.by_status ?? {}).map(
        ([status, count]) => ({
            name: STATUS_LABELS[status] ?? status,
            value: count ?? 0,
            rawStatus: status,
        })
    );

    const getColorForStatus = (status, index) =>
        STATUS_COLORS[status] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

    const paymentBarData = [
        { name: 'Payments', count: stats?.payments?.total_count ?? 0, amount: stats?.payments?.total_amount ?? 0 },
    ];

    const paymentData = stats?.payments?.monthly_trend

    const handleRelativeClick = async () => {
        try {
            if (customer.relative_id || customer.id) {
                const response = await axiosInstance.post("/api/customer/relatives/", {
                    row_id: customer.id,
                    relative_id: customer.relative,
                });
    
                const relativesData = response.data;
                setRelatives(relativesData);
                setInitCust(customer);
                setRelativesDialogVisible(true); 
            } else {
                handleApiError({ Error: "No relatives found for this customer." });
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <div className="p-grid">
            <CustomerOverview
                visible={relativesDialogVisible}
                onHide={() => {setRelativesDialogVisible(false)}}
                customer={customer}
                handleCreateInvoice={handleCreateInvoice}
                handleCreatePrescription={handleCreatePrescription} 
                handleUpdateCustomer={handleUpdateCustomer}
                paymentData={paymentData}
                relatives={relatives}
                handleRelativeClick={handleRelativeClick}
                handleAddRelative={handleAddRelative}
                initCust={initCust}
                paymentBarData={paymentBarData}
                invoicePieData={invoicePieData}
                getColorForStatus={getColorForStatus}
                stats={stats}
                portalLink={portalLink}
                handleGrantHistory={handleGrantHistory}
            />
            <div className="p-col-12 p-md-8">
                {crmOnly && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0.5rem 0' }}>
                        <Button
                            icon="pi pi-send"
                            label="Send Order Ready SMS"
                            className="p-button-outlined"
                            disabled={!customer?.phone_number || sendingOrderReady}
                            loading={sendingOrderReady}
                            onClick={handleSendOrderReady}
                            tooltip={
                                customer?.phone_number
                                    ? 'Send an "order ready for pickup" SMS to this customer'
                                    : 'Customer has no phone number on record'
                            }
                            tooltipOptions={{ position: 'top' }}
                        />
                    </div>
                )}
                <TabView className="tabview-scroll">
                    {crmOnly && (
                        <TabPanel header="Manual Refill Reminders">
                            <ManualRefillReminders customerId={id} />
                        </TabPanel>
                    )}
                    <TabPanel header="Refill Reminders">
                        <RefillReminders customerId={id} />
                    </TabPanel>
                    <TabPanel header="Prescriptions">
                        <PrescriptionHistory customerId={id} />
                    </TabPanel>
                    <TabPanel header="Invoices">
                        <InvoiceHistory customerId={id} />
                    </TabPanel>
                    <TabPanel header="Notes">
                        <CustomerNotes customerId={id} />
                    </TabPanel>
                    <TabPanel header="Attachments">
                        <CustomerAttachments customerId={id} />
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );
};

export default CustomerDashboard;